import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import styled, { keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const PrivacyPolicyContainer = styled.div`
  padding: 140px 0 60px;
  
  @media (max-width: 768px) {
    padding: 100px 0 40px;
  }
  background: #ffffff;
  min-height: 100vh;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 5%;
`;

const PageTitle = styled.h1`
  text-align: center;
  font-size: 4.5rem;
  color: #2c2c2c;
  margin-bottom: 30px;
  
  letter-spacing: -2px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #0a1628, #1a2744);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const PageSubtitle = styled.p`
  text-align: center;
  font-size: 1.4rem;
  color: #6c757d;
  margin-bottom: 120px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
  
`;

// Hero Section
const HeroSection = styled.section`
  position: relative;
  height: 500px;
  border-radius: 25px;
  overflow: hidden;
  margin-bottom: 120px;
  
  &.animate-in {
    animation: ${scaleIn} 0.8s ease-out;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('https://images.unsplash.com/photo-1503387762-592deb58ef4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2126&q=80') no-repeat center center;
    background-size: cover;
    transition: transform 0.5s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(10, 22, 40, 0.85), rgba(26, 39, 68, 0.7));
  }
  
  &:hover::before {
    transform: scale(1.05);
  }
`;

const HeroContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 2;
  color: white;
  
  h2 {
    font-size: 3.5rem;
    
    margin-bottom: 25px;
    letter-spacing: -1.5px;
  
    color: #ffffff;}
  
  p {
    font-size: 1.3rem;
    
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto;
    opacity: 0.95;
  
    color: #ffffff;}
  
  @media (max-width: 768px) {
    h2 {
      font-size: 2.5rem;
    }
    
    p {
      font-size: 1.1rem;
    }
  }
`;

// Content Section
const ContentSection = styled.section`
  margin-bottom: 120px;
  
  &.animate-in {
    .content-grid .content-card:nth-child(1) { animation: ${slideInLeft} 0.6s ease-out 0.1s both; }
    .content-grid .content-card:nth-child(2) { animation: ${slideInLeft} 0.6s ease-out 0.3s both; }
    .content-grid .content-card:nth-child(3) { animation: ${slideInLeft} 0.6s ease-out 0.5s both; }
    .content-grid .content-card:nth-child(4) { animation: ${slideInLeft} 0.6s ease-out 0.7s both; }
    .content-grid .content-card:nth-child(5) { animation: ${slideInLeft} 0.6s ease-out 0.9s both; }
    .content-grid .content-card:nth-child(6) { animation: ${slideInLeft} 0.6s ease-out 1.1s both; }
  }
`;

const SectionTitle = styled.h2`
  font-size: 3.5rem;
  
  color: #2c2c2c;
  margin-bottom: 80px;
  text-align: center;
  letter-spacing: -1.5px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
  max-width: 1000px;
  margin: 0 auto;
`;

const ContentCard = styled.div`
  background: white;
  padding: 50px 40px;
  border-radius: 20px;
  border: 1px solid rgba(10, 22, 40, 0.08);
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, #0a1628, #1a2744);
    transform: scaleY(0);
    transition: transform 0.4s ease;
  }
  
  &:hover {
    transform: translateX(10px);
    box-shadow: 0 20px 40px rgba(10, 22, 40, 0.12);
    border-color: rgba(10, 22, 40, 0.15);
  }
  
  &:hover::before {
    transform: scaleY(1);
  }
  
  .icon {
    font-size: 2.5rem;
    color: #0a1628;
    margin-bottom: 25px;
    transition: all 0.4s ease;
  }
  
  &:hover .icon {
    transform: scale(1.1);
    color: #1a2744;
  }
  
  h3 {
    font-size: 1.8rem;
    color: #2c2c2c;
    margin-bottom: 20px;
    font-weight: 500;
    letter-spacing: -0.5px;
  }
  
  p {
    color: #6c757d;
    line-height: 1.8;
    font-size: 1.1rem;
    
    margin-bottom: 20px;
  }
  
  ul {
    margin: 20px 0;
    padding-left: 0;
    list-style: none;
    
    li {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      margin-bottom: 12px;
      color: #6c757d;
      font-size: 1rem;
      line-height: 1.6;
      
      &::before {
        content: '•';
        color: #0a1628;
        font-weight: bold;
        margin-top: 2px;
      }
    }
  }
  
  .highlight {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    padding: 25px;
    border-radius: 12px;
    margin: 25px 0;
    border-left: 4px solid #0a1628;
    
    p {
      margin: 0;
      font-weight: 400;
      color: #495057;
    }
  }
`;

// Contact Section
const ContactSection = styled.section`
  background: linear-gradient(135deg, #0a1628 0%, #1a2744 100%);
  padding: 100px 0;
  border-radius: 30px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
  
  &.animate-in {
    animation: ${fadeInUp} 0.8s ease-out;
  }
  
  h2 {
    font-size: 3rem;
    
    color: white;
    margin-bottom: 25px;
    letter-spacing: -1.5px;
  }
  
  p {
    font-size: 1.3rem;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.7;
    max-width: 600px;
    margin: 0 auto 40px;
    
  }
  
  .contact-btn {
    background: rgba(255, 255, 255, 0.2);
    border: 2px solid white;
    color: white;
    padding: 15px 40px;
    border-radius: 30px;
    font-weight: 500;
    font-size: 1.1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    text-decoration: none;
    display: inline-block;
    backdrop-filter: blur(10px);
    letter-spacing: 0.5px;
    
    &:hover {
      background: white;
      color: #0a1628;
      transform: translateY(-3px);
    }
  }
  
  @media (max-width: 768px) {
    h2 {
      font-size: 2.2rem;
    }
    
    p {
      font-size: 1.1rem;
    }
  }
`;

const PrivacyPolicy = () => {
  const [animatedSections, setAnimatedSections] = useState(new Set());
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimatedSections(prev => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <PrivacyPolicyContainer>
      <Container>
        <PageTitle>{t('privacyPolicy.title')}</PageTitle>
        <PageSubtitle>
          {t('privacyPolicy.subtitle')}
        </PageSubtitle>

        <HeroSection 
          id="hero-section" 
          data-animate
          className={animatedSections.has('hero-section') ? 'animate-in' : ''}
        >
          <HeroContent>
            <h2>{t('privacyPolicy.heroTitle')}</h2>
            <p>
              {t('privacyPolicy.heroDesc')}
            </p>
          </HeroContent>
        </HeroSection>

        <ContentSection 
          id="content-section" 
          data-animate
          className={animatedSections.has('content-section') ? 'animate-in' : ''}
        >
          <SectionTitle>{t('privacyPolicy.contentTitle')}</SectionTitle>
          <ContentGrid className="content-grid">
            <ContentCard className="content-card">
              <div className="icon">📊</div>
              <h3>{t('privacyPolicy.cont1Title')}</h3>
              <p>
                {t('privacyPolicy.cont1Desc')}
              </p>
              <ul>
                <li>{t('privacyPolicy.cont1f1')}</li>
                <li>{t('privacyPolicy.cont1f2')}</li>
                <li>{t('privacyPolicy.cont1f3')}</li>
                <li>{t('privacyPolicy.cont1f4')}</li>
              </ul>
            </ContentCard>

            <ContentCard className="content-card">
              <div className="icon">🔒</div>
              <h3>{t('privacyPolicy.cont2Title')}</h3>
              <p>
                {t('privacyPolicy.cont2Desc')}
              </p>
              <ul>
                <li>{t('privacyPolicy.cont2f1')}</li>
                <li>{t('privacyPolicy.cont2f2')}</li>
                <li>{t('privacyPolicy.cont2f3')}</li>
                <li>{t('privacyPolicy.cont2f4')}</li>
              </ul>
            </ContentCard>

            <ContentCard className="content-card">
              <div className="icon">🎯</div>
              <h3>{t('privacyPolicy.cont3Title')}</h3>
              <p>
                {t('privacyPolicy.cont3Desc')}
              </p>
              <ul>
                <li>{t('privacyPolicy.cont3f1')}</li>
                <li>{t('privacyPolicy.cont3f2')}</li>
                <li>{t('privacyPolicy.cont3f3')}</li>
                <li>{t('privacyPolicy.cont3f4')}</li>
              </ul>
              <div className="highlight">
                <p>
                  {t('privacyPolicy.cont3Highlight')}
                </p>
              </div>
            </ContentCard>

            <ContentCard className="content-card">
              <div className="icon">🍪</div>
              <h3>{t('privacyPolicy.cont4Title')}</h3>
              <p>
                {t('privacyPolicy.cont4Desc')}
              </p>
              <ul>
                <li>{t('privacyPolicy.cont4f1')}</li>
                <li>{t('privacyPolicy.cont4f2')}</li>
                <li>{t('privacyPolicy.cont4f3')}</li>
                <li>{t('privacyPolicy.cont4f4')}</li>
              </ul>
              <p>
                {t('privacyPolicy.cont4Footer')}
              </p>
            </ContentCard>

            <ContentCard className="content-card">
              <div className="icon">⚖️</div>
              <h3>{t('privacyPolicy.cont5Title')}</h3>
              <p>
                {t('privacyPolicy.cont5Desc')}
              </p>
              <ul>
                <li>{t('privacyPolicy.cont5f1')}</li>
                <li>{t('privacyPolicy.cont5f2')}</li>
                <li>{t('privacyPolicy.cont5f3')}</li>
                <li>{t('privacyPolicy.cont5f4')}</li>
                <li>{t('privacyPolicy.cont5f5')}</li>
              </ul>
              <div className="highlight">
                <p>
                  {t('privacyPolicy.cont5Highlight')}
                </p>
              </div>
            </ContentCard>

            <ContentCard className="content-card">
              <div className="icon">📞</div>
              <h3>{t('privacyPolicy.cont6Title')}</h3>
              <p>
                {t('privacyPolicy.cont6Desc')}
              </p>
              <ul>
                <li>{t('privacyPolicy.cont6f1')}</li>
                <li>{t('privacyPolicy.cont6f2')}</li>
                <li>{t('privacyPolicy.cont6f3')}</li>
              </ul>
              <p>
                {t('privacyPolicy.cont6Footer')}
              </p>
            </ContentCard>
          </ContentGrid>
        </ContentSection>

        <ContactSection 
          id="contact-section" 
          data-animate
          className={animatedSections.has('contact-section') ? 'animate-in' : ''}
        >
          <h2>{t('privacyPolicy.contactTitle')}</h2>
          <p>
            {t('privacyPolicy.contactDesc')}
          </p>
          <a href="/iletisim" className="contact-btn">
            {t('privacyPolicy.contactBtn')}
          </a>
        </ContactSection>
      </Container>
    </PrivacyPolicyContainer>
  );
};

export default PrivacyPolicy;